/**
 * ПОЛНЫЙ ПУТЬ: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminUsers\AdminUserRoles.js
 * ВЕРСИЯ: 1.0.0
 * ДАТА СОЗДАНИЯ: 15 ноября 2025
 * ВРЕМЯ: 15:35
 * 
 * ОПИСАНИЕ: Компонент для управления ролями пользователей
 * - Смена роли: user -> business -> admin
 * - Просмотр прав каждой роли
 * - Изменение уровня (tier) пользователя
 */

import React, { useState, useEffect } from 'react';
import './AdminUsers.css';

const AdminUserRoles = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newRole, setNewRole] = useState('user');
  const [newTier, setNewTier] = useState(0);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://qr-money.onrender.com/api/admin/users', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Ошибка загрузки пользователей');
      
      const data = await response.json();
      const userList = data.data || data.users || data || [];
      setUsers(Array.isArray(userList) ? userList : []);
    } catch (err) {
      console.error('❌ Ошибка:', err);
      setError(err.message);
      setUsers(getMockUsers());
    } finally {
      setLoading(false);
    }
  };

  const getMockUsers = () => [
    { id: 1, email: 'alice@test.com', role: 'user', tier: 0, status: 'active' },
    { id: 2, email: 'business@test.com', role: 'business', tier: 1, status: 'active' },
    { id: 3, email: 'admin@test.com', role: 'admin', tier: 10, status: 'active' }
  ];

  const updateUserRole = async (userId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`https://qr-money.onrender.com/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          role: newRole,
          tier: newTier
        })
      });

      if (!response.ok) throw new Error('Ошибка обновления роли');
      
      alert('✅ Роль обновлена успешно!');
      fetchUsers();
      setSelectedUserId(null);
    } catch (err) {
      alert(`❌ Ошибка: ${err.message}`);
    }
  };

  // Описание ролей и их прав
  const roleDescriptions = {
    user: {
      name: 'Обычный пользователь',
      icon: '🔵',
      permissions: ['Создание QR кодов', 'Сканирование QR', 'История транзакций', 'Управление профилем'],
      limits: { weekly_DAR: 150, daily_qr: 100 }
    },
    business: {
      name: 'Бизнес-аккаунт',
      icon: '🟢',
      permissions: ['Все как User', 'Массовое создание QR', 'Custom templates', 'Аналитика', 'Team management'],
      limits: { weekly_DAR: 10000, daily_qr: 1000 }
    },
    admin: {
      name: 'Администратор',
      icon: '🔴',
      permissions: ['Полный контроль', 'Управление пользователями', 'TRON кошельки', 'Anti-fraud', 'Все остальное'],
      limits: { weekly_DAR: 'Unlimited', daily_qr: 'Unlimited' }
    }
  };

  if (loading) return <div className="admin-container">⏳ Загрузка...</div>;

  return (
    <div className="admin-container">
      <h1>👥 Управление ролями пользователей</h1>

      {error && <div className="error-banner">⚠️ {error}</div>}

      {/* СПРАВКА ПО РОЛЯМ */}
      <div className="roles-info">
        <h2>📋 Роли и их права</h2>
        <div className="roles-grid">
          {Object.entries(roleDescriptions).map(([roleKey, role]) => (
            <div key={roleKey} className="role-card">
              <h3>{role.icon} {role.name}</h3>
              <div className="permissions-list">
                {role.permissions.map((perm, idx) => (
                  <div key={idx}>✓ {perm}</div>
                ))}
              </div>
              <div className="limits">
                <strong>Лимиты:</strong>
                <div>DAR/неделю: {role.limits.weekly_DAR}</div>
                <div>QR/день: {role.limits.daily_qr}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ТАБЛИЦА ПОЛЬЗОВАТЕЛЕЙ */}
      <h2>📊 Текущие пользователи</h2>
      <table className="users-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Email</th>
            <th>Текущая роль</th>
            <th>Уровень (Tier)</th>
            <th>Статус</th>
            <th>Действие</th>
          </tr>
        </thead>
        <tbody>
          {users.map(user => (
            <tr key={user.id}>
              <td>{user.id}</td>
              <td>{user.email}</td>
              <td>
                <span className={`role-badge role-${user.role}`}>
                  {roleDescriptions[user.role]?.icon} {user.role}
                </span>
              </td>
              <td>{user.tier || 0}</td>
              <td>
                <span className={`status-badge status-${user.status}`}>
                  {user.status === 'active' ? '✅' : '❌'} {user.status}
                </span>
              </td>
              <td>
                <button
                  className="action-btn edit-btn"
                  onClick={() => {
                    setSelectedUserId(user.id);
                    setNewRole(user.role);
                    setNewTier(user.tier || 0);
                  }}
                >
                  ✏️ Изменить
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* МОДАЛЬ ИЗМЕНЕНИЯ РОЛИ */}
      {selectedUserId && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>🔄 Изменить роль пользователя #{selectedUserId}</h2>
            
            <div className="form-group">
              <label>Выберите новую роль:</label>
              <select 
                value={newRole} 
                onChange={(e) => setNewRole(e.target.value)}
                className="form-select"
              >
                <option value="user">🔵 Обычный пользователь</option>
                <option value="business">🟢 Бизнес-аккаунт</option>
                <option value="admin">🔴 Администратор</option>
              </select>
            </div>

            <div className="form-group">
              <label>Уровень (Tier):</label>
              <input
                type="number"
                min="0"
                max="10"
                value={newTier}
                onChange={(e) => setNewTier(parseInt(e.target.value))}
                className="form-input"
              />
              <small>Диапазон: 0-10</small>
            </div>

            {/* ПРЕДПРОСМОТР НОВЫХ ПРАВ */}
            <div className="preview-box">
              <h4>📋 Новые права:</h4>
              <div className="permissions-list">
                {roleDescriptions[newRole]?.permissions.map((perm, idx) => (
                  <div key={idx}>✓ {perm}</div>
                ))}
              </div>
            </div>

            <div className="modal-buttons">
              <button
                className="btn btn-primary"
                onClick={() => updateUserRole(selectedUserId)}
              >
                💾 Сохранить
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedUserId(null)}
              >
                ❌ Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .roles-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin: 20px 0;
        }

        .role-card {
          border: 2px solid #ddd;
          border-radius: 8px;
          padding: 15px;
          background: #f9f9f9;
        }

        .role-card h3 {
          margin-top: 0;
          color: #333;
        }

        .permissions-list {
          margin: 10px 0;
          font-size: 14px;
          line-height: 1.8;
        }

        .limits {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid #ddd;
          font-size: 13px;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          padding: 30px;
          border-radius: 8px;
          max-width: 500px;
          width: 90%;
        }

        .form-group {
          margin: 20px 0;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: bold;
        }

        .form-select, .form-input {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }

        .preview-box {
          background: #f0f0f0;
          padding: 15px;
          border-radius: 4px;
          margin: 20px 0;
        }

        .modal-buttons {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          margin-top: 20px;
        }

        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .btn-primary {
          background: #007bff;
          color: white;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .role-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
        }

        .role-user {
          background: #d4e6ff;
          color: #0056b3;
        }

        .role-business {
          background: #d4edda;
          color: #155724;
        }

        .role-admin {
          background: #f8d7da;
          color: #721c24;
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
        }

        .status-active {
          background: #d4edda;
          color: #155724;
        }

        .status-blocked {
          background: #f8d7da;
          color: #721c24;
        }
      `}</style>
    </div>
  );
};

export default AdminUserRoles;
